import React, { FC, Fragment, useCallback, useMemo, useState } from 'react';
import {
  add,
  addMonths,
  min as minDate,
  max as maxDate,
  sub,
  format
} from 'date-fns';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from '@/elements';
import { CalendarProps } from './Calendar';
import { CalendarDays } from './CalendarDays';
import { Divider } from '@/layout';
import { H4 } from '@/typography';
import { useComponentTheme } from '@/utils';
import { CalendarRangeTheme } from './CalendarRangeTheme';
import { CalendarPresets, PresetOption } from './CalendarPresets';

export interface CalendarRangeProps extends Omit<
  CalendarProps,
  'value' | 'isRange' | 'onViewChange' | 'theme' | 'showTime'
> {
  /**
   * The selected date(s) for the calendar.
   */
  value?:
    | [Date, Date]
    | [undefined, undefined]
    | [Date | undefined]
    | undefined;

  /**
   * The number of calendar months to display.
   * @default 2
   */
  monthsToDisplay?: number;

  /**
   * Defaults view to show past or future months if multiple months shown.
   * @default 'future'
   */
  direction?: 'past' | 'future';

  /**
   * The text or icon to use for next year.
   * @default '»'
   */
  nextYearArrow?: React.ReactNode | string;

  /**
   * The text or icon to use for previous year.
   * @default '«'
   */
  previousYearArrow?: React.ReactNode | string;

  /**
   * The format of the date displayed in the calendar header.
   * @default 'MMMM'
   */
  headerDateFormat?: string;

  /**
   * Theme for the CalendarRange.
   */
  theme?: CalendarRangeTheme;

  /**
   * Preset options for the calendar.
   */
  preset?: PresetOption[];
}

export const CalendarRange: FC<CalendarRangeProps> = ({
  min,
  max,
  value,
  disabled,
  previousArrow = '‹',
  previousYearArrow = '«',
  nextArrow = '›',
  nextYearArrow = '»',
  showDayOfWeek,
  animated = true,
  onChange,
  monthsToDisplay = 2,
  direction = 'future',
  headerDateFormat = 'MMMM',
  theme: customTheme,
  preset,
  ...rest
}) => {
  const theme: CalendarRangeTheme = useComponentTheme(
    'calendarRange',
    customTheme
  );
  const date = useMemo(
    () => (Array.isArray(value) ? value[0] : new Date()),
    [value]
  );
  const rangeStart = useMemo(() => (value ? value[0] : undefined), [value]);
  const rangeEnd = useMemo(() => (value ? value[1] : undefined), [value]);

  const [viewValue, setViewValue] = useState<Date>(date || new Date());
  const [scrollDirection, setScrollDirection] = useState<
    'forward' | 'back' | null
  >(null);
  const [hoveringDate, setHoveringDate] = useState<Date | null>(null);

  const displayMonths = Array.from(Array(monthsToDisplay).keys());
  const showPast = direction === 'past';
  if (direction === 'past') {
    displayMonths.reverse();
  }

  const dateChangeHandler = useCallback(
    (date: Date) => {
      if (!rangeStart) {
        onChange?.([date, undefined]);
      } else if (!rangeEnd) {
        const range = [rangeStart, date];
        onChange?.([minDate(range), maxDate(range)]);
      } else {
        onChange?.([date, undefined]);
      }
    },
    [onChange, rangeEnd, rangeStart]
  );

  const previousClickHandler = useCallback(() => {
    setScrollDirection('back');
    setViewValue(sub(viewValue, { months: 1 }));
  }, [viewValue]);

  const previousYearClickHandler = useCallback(() => {
    setScrollDirection('back');
    setViewValue(sub(viewValue, { years: 1 }));
  }, [viewValue]);

  const nextClickHandler = useCallback(() => {
    setScrollDirection('forward');
    setViewValue(add(viewValue, { months: 1 }));
  }, [viewValue]);

  const nextYearClickHandler = useCallback(() => {
    setScrollDirection('forward');
    setViewValue(add(viewValue, { years: 1 }));
  }, [viewValue]);

  const xAnimation = useMemo(() => {
    switch (scrollDirection) {
      case 'forward':
        return '100%';
      case 'back':
        return '-100%';
      default:
        return 0;
    }
  }, [scrollDirection]);

  const handlePresetChange = useCallback(
    (newValue: [Date, Date]) => {
      setScrollDirection(null);
      setViewValue(newValue[1]);
      onChange?.(newValue);
    },
    [onChange]
  );

  return (
    <div className={theme.base}>
      <div className="relative flex">
        {preset && (
          <div className={`flex items-center gap-1 ${theme.presets.wrapper}`}>
            <CalendarPresets
              options={preset}
              showTime={false}
              className="min-w-[100px] w-auto flex-shrink-0"
              value={
                value && value.length >= 2
                  ? [value[0] as Date, value[1] as Date]
                  : undefined
              }
              onChange={handlePresetChange}
            />
            <Divider orientation="vertical" className={theme.presets.divider} />
          </div>
        )}
        <div className="flex-1">
          <header className={theme.header.base}>
            <div className="flex items-center gap-2.5">
              <Button
                variant="text"
                disabled={disabled}
                onClick={previousYearClickHandler}
                className={theme.header.prev}
                disablePadding
                aria-label="Previous year"
              >
                {previousYearArrow}
              </Button>
              <Button
                variant="text"
                disabled={disabled}
                onClick={previousClickHandler}
                className={theme.header.prev}
                disablePadding
                aria-label="Previous month"
              >
                {previousArrow}
              </Button>
            </div>
            <H4 className={theme.title}>
              {displayMonths.map(i => (
                <span
                  key={addMonths(viewValue, showPast ? -i : i).toDateString()}
                >
                  {format(
                    addMonths(viewValue, showPast ? -i : i),
                    headerDateFormat
                  )}
                </span>
              ))}
            </H4>
            <div className="flex items-center gap-2.5">
              <Button
                variant="text"
                disabled={disabled}
                onClick={nextClickHandler}
                className={theme.header.next}
                disablePadding
                aria-label="Next month"
              >
                {nextArrow}
              </Button>
              <Button
                variant="text"
                disabled={disabled}
                onClick={nextYearClickHandler}
                className={theme.header.next}
                disablePadding
                aria-label="Next year"
              >
                {nextYearArrow}
              </Button>
            </div>
          </header>
          <Divider />
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 1 }}
              transition={{
                x: { type: animated ? 'keyframes' : false },
                opacity: { duration: 0.2, type: animated ? 'tween' : false },
                scale: { type: animated ? 'tween' : false }
              }}
            >
              <div className={theme.content}>
                {displayMonths.map((offset, idx) => (
                  <Fragment key={`calendar-${offset}`}>
                    <CalendarDays
                      value={addMonths(viewValue, showPast ? -offset : offset)}
                      min={min}
                      max={max}
                      disabled={disabled}
                      current={[rangeStart, rangeEnd]}
                      showDayOfWeek={showDayOfWeek}
                      xAnimation={xAnimation}
                      animated={animated}
                      hover={hoveringDate}
                      onHover={setHoveringDate}
                      hidePrevMonthDays={idx > 0}
                      hideNextMonthDays={idx < monthsToDisplay - 1}
                      onChange={dateChangeHandler}
                      isRange
                      {...rest}
                    />
                  </Fragment>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
